import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Brain, CheckCircle2, XCircle, Crown, RotateCcw, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Quiz() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState("all");
  const [mode, setMode] = useState("study");
  const [usage, setUsage] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [result, setResult] = useState(null);
  const [started, setStarted] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadUsage = () => api.get("/quiz/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/quiz/chapters").then((r) => setChapters(r.data)).catch(() => {});
    loadUsage();
  }, []);

  const start = async () => {
    setLoading(true);
    try {
      const params = { limit: 10, mode };
      if (chapterId !== "all") params.chapter_id = chapterId;
      const { data } = await api.get("/quiz/questions", { params });
      if (!data.length) { toast.info("No new questions available for this chapter right now."); setLoading(false); return; }
      setQuestions(data); setIdx(0); setAnswers({}); setRevealed({}); setResult(null); setStarted(true);
    } catch (e) {
      if (e.response?.data?.detail === "daily_limit_reached") setLimitOpen(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const select = (qid, i) => {
    if (revealed[qid]) return;
    setAnswers({ ...answers, [qid]: i });
    if (mode === "study") setRevealed({ ...revealed, [qid]: true });
  };

  const submit = async () => {
    const payload = { chapter_id: chapterId !== "all" ? chapterId : null, answers: questions.map((q) => ({ question_id: q.id, selected_index: answers[q.id] ?? -1 })) };
    try {
      const { data } = await api.post("/quiz/submit", payload);
      setResult(data);
      loadUsage();
    } catch (e) {
      if (e.response?.data?.detail === "daily_limit_reached") setLimitOpen(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const q = questions[idx];
  const answeredCount = Object.keys(answers).length;

  // RESULT SCREEN
  if (result) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="quiz-result">
            <div className={`h-20 w-20 rounded-full mx-auto flex items-center justify-center ${result.score >= 70 ? "bg-emerald-50" : "bg-amber-50"}`}>
              <span className={`font-heading text-3xl font-extrabold ${result.score >= 70 ? "text-emerald-600" : "text-amber-600"}`}>{result.score}%</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0A192F] mt-4">You scored {result.correct} / {result.total}</h2>
            <p className="text-slate-500 mt-1">{result.score >= 70 ? "Great work — you're on track!" : "Keep practicing to boost your readiness."}</p>
            <div className="mt-6 space-y-3 text-left">
              {result.results.map((r, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-4" data-testid={`quiz-review-${i}`}>
                  <div className="flex items-start gap-2">
                    {r.is_correct ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />}
                    <div>
                      <div className="text-sm font-medium text-[#0A192F]">{r.question}</div>
                      <div className="text-xs text-slate-600 mt-1"><span className="font-semibold">Explanation:</span> {r.explanation}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" className="flex-1" onClick={() => { setStarted(false); setResult(null); }} data-testid="quiz-again-btn"><RotateCcw className="h-4 w-4 mr-1" /> New Quiz</Button>
              <Button className="flex-1 bg-[#0A192F] hover:bg-[#1E293B] text-white" onClick={() => navigate("/dashboard")}>Dashboard</Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // QUIZ IN PROGRESS
  if (started && q) {
    const sel = answers[q.id];
    const show = revealed[q.id];
    return (
      <Layout showFooter={false}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-sm text-slate-500">Question {idx + 1} / {questions.length}</span>
            <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold uppercase">{mode} mode</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-200 mb-6 overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${((idx + 1) / questions.length) * 100}%` }} /></div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="quiz-card">
            <h2 className="font-heading text-lg sm:text-xl font-semibold text-[#0A192F] leading-snug">{q.question}</h2>
            <div className="mt-6 space-y-3">
              {q.choices.map((c, i) => {
                let cls = "border-slate-200 hover:border-amber-300 bg-white";
                if (show) {
                  if (i === q.correct_index) cls = "border-emerald-400 bg-emerald-50";
                  else if (i === sel) cls = "border-red-300 bg-red-50";
                  else cls = "border-slate-200 bg-white opacity-70";
                } else if (i === sel) cls = "border-amber-400 bg-amber-50";
                return (
                  <button key={i} onClick={() => select(q.id, i)} className={`w-full text-left rounded-xl border-2 p-4 flex items-center gap-3 transition ${cls}`} data-testid={`quiz-option-${String.fromCharCode(97 + i)}`}>
                    <span className="h-7 w-7 rounded-full border border-slate-300 flex items-center justify-center text-sm font-semibold shrink-0">{String.fromCharCode(65 + i)}</span>
                    <span className="text-slate-700 text-sm">{c}</span>
                  </button>
                );
              })}
            </div>
            {show && (
              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4" data-testid="quiz-explanation">
                <p className="text-sm text-slate-700"><span className="font-semibold text-[#0A192F]">Explanation: </span>{q.explanation}</p>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-6">
            <Button variant="ghost" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>Previous</Button>
            {idx < questions.length - 1 ? (
              <Button className="bg-[#0A192F] hover:bg-[#1E293B] text-white" onClick={() => setIdx(idx + 1)} data-testid="quiz-next-btn">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" disabled={answeredCount === 0} onClick={submit} data-testid="quiz-submit-btn">Submit Quiz</Button>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // START SCREEN
  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="h-7 w-7 text-amber-600" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">Practice Center</h1>
        </div>
        <p className="text-slate-500 mb-6">Run a Knowledge Check with exam-style questions and instant explanations.</p>

        {usage && !usage.unlimited && (
          <div className="rounded-xl bg-[#0A192F] text-white p-4 mb-6 flex items-center justify-between" data-testid="quiz-usage-bar">
            <span className="text-sm">{usage.remaining} of {usage.limit} new questions remaining today</span>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("/upgrade")}><Crown className="h-3.5 w-3.5 mr-1" /> Unlimited</Button>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700">Chapter</label>
            <Select value={chapterId} onValueChange={setChapterId}>
              <SelectTrigger className="mt-1.5" data-testid="quiz-chapter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Chapters</SelectItem>
                {chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.title} ({c.question_count})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Mode</label>
            <div className="grid grid-cols-2 gap-3 mt-1.5">
              <button onClick={() => setMode("study")} className={`rounded-xl border-2 p-4 text-left ${mode === "study" ? "border-amber-400 bg-amber-50" : "border-slate-200"}`} data-testid="quiz-mode-study">
                <div className="font-semibold text-[#0A192F]">Study Mode</div>
                <div className="text-xs text-slate-500">Instant explanations after each answer</div>
              </button>
              <button onClick={() => setMode("exam")} className={`rounded-xl border-2 p-4 text-left ${mode === "exam" ? "border-amber-400 bg-amber-50" : "border-slate-200"}`} data-testid="quiz-mode-exam">
                <div className="font-semibold text-[#0A192F]">Exam Mode</div>
                <div className="text-xs text-slate-500">Answers revealed at the end</div>
              </button>
            </div>
          </div>
          <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11" onClick={start} disabled={loading} data-testid="quiz-start-btn">
            {loading ? "Loading…" : "Start Quiz"}
          </Button>
        </div>
      </div>

      <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
        <DialogContent data-testid="quiz-limit-modal">
          <DialogHeader>
            <div className="h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center mb-2"><Crown className="h-6 w-6 text-amber-600" /></div>
            <DialogTitle className="font-heading">You've hit today's free limit</DialogTitle>
            <DialogDescription>Free accounts get 10 practice questions per day. Upgrade to Full Access for unlimited quizzes, the complete workbook, and the full audio library.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitOpen(false)}>Maybe later</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("/upgrade")} data-testid="quiz-limit-upgrade-btn">Unlock for $49.95</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
